"use client";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Vehicle } from "@/types";
import { AlertCircle, Calendar } from "lucide-react";

interface DeletionBlockedDetails {
  hasPendingAppointments: boolean;
  pendingAppointments: Array<{
    id: string;
    date: string;
    status: string;
    bookingRef: string | null;
  }>;
  suggestions: string[];
}

interface VehicleDeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vehicle: Vehicle | null;
  onConfirm: () => void;
  isBlocked?: boolean;
  blockDetails?: DeletionBlockedDetails | null;
  isLoading?: boolean;
}

export function VehicleDeleteDialog({
  open,
  onOpenChange,
  vehicle,
  onConfirm,
  isBlocked = false,
  blockDetails = null,
  isLoading = false,
}: VehicleDeleteDialogProps) {
  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            {isBlocked && <AlertCircle className="h-5 w-5 text-destructive" />}
            {isBlocked ? "Cannot Delete Vehicle" : "Are you sure?"}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-4">
              {isBlocked && blockDetails ? (
                <>
                  <p className="text-sm text-muted-foreground">
                    {vehicle?.make} {vehicle?.model} ({vehicle?.plateNumber})
                    cannot be deleted due to the following:
                  </p>

                  {/* Pending Appointments */}
                  {blockDetails.hasPendingAppointments && (
                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-900 dark:bg-blue-950">
                      <div className="flex items-center gap-2 font-medium text-blue-800 dark:text-blue-200">
                        <Calendar className="h-4 w-4" />
                        Pending Appointments
                      </div>
                      <ul className="mt-2 space-y-1 text-sm text-blue-700 dark:text-blue-300">
                        {blockDetails.pendingAppointments
                          .slice(0, 5)
                          .map((apt) => (
                            <li
                              key={apt.id}
                              className="flex items-center gap-2"
                            >
                              <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
                              {formatDate(apt.date)} - {apt.status}
                              {apt.bookingRef && ` (${apt.bookingRef})`}
                            </li>
                          ))}
                        {blockDetails.pendingAppointments.length > 5 && (
                          <li className="text-blue-600 dark:text-blue-400">
                            +{blockDetails.pendingAppointments.length - 5} more
                          </li>
                        )}
                      </ul>
                    </div>
                  )}

                  {/* Suggestions */}
                  {blockDetails.suggestions.length > 0 && (
                    <div className="rounded-lg border bg-muted/50 p-3">
                      <p className="text-sm font-medium">Suggestions:</p>
                      <ul className="mt-1 space-y-1 text-sm text-muted-foreground">
                        {blockDetails.suggestions.map((suggestion, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="mt-1.5 h-1 w-1 rounded-full bg-muted-foreground" />
                            {suggestion}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              ) : (
                <p>
                  This will permanently delete {vehicle?.make} {vehicle?.model}{" "}
                  and cannot be undone.
                </p>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>
            {isBlocked ? "Close" : "Cancel"}
          </AlertDialogCancel>
          {!isBlocked && (
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                onConfirm();
              }}
              className="bg-destructive text-destructive-foreground"
              disabled={isLoading}
            >
              {isLoading ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
